import React, { useEffect, useState } from "react";
import { Tabs } from "antd";
import { useTranslation } from "react-i18next";
import { getDataRequest } from "../../api/serviceMethods";
import { EXPORT_ORDER_URL, ORDERS_URL } from "../../api/URLs";
import TablePendingShipment from "./TablePendingShipment";
import TableShipped from "./TableShipped";
import { formatDateTime } from "../../utils/dateFormatUtils";
import * as XLSX from "xlsx";

const TableProductPurchasePage = ({ setLoading, messageModalRef }) => {
    const { t } = useTranslation();
    const [dates, setDates] = useState([null, null]);
    const [paymentData, setPaymentData] = useState([])
    const [exportData, setExportData] = useState([])
    const [memberType, setMemberType] = useState('pending');
    const [keyword, setKeyword] = useState('');
    const [isSearch, setIsSearch] = useState(true)
    const [isSearchExport, setIsSearchExport] = useState(false)
    const [dataObject, setDataObject] = useState({
        type: "",
        method: ""
    })
    const [tableParams, setTableParams] = useState({
        pagination: {
            current: 1,
            pageSize: 10,
            total: 0,
        },
    });

    const handleTabChange = (activeKey) => {
        setTableParams(prevParams => ({
            ...prevParams,
            pagination: {
                ...prevParams.pagination,
                current: 1,
            },
        }));
        if (activeKey === 'pendingList') {
            setMemberType('pending');
            setKeyword("")
            setDates([null, null]);
        }
        else if (activeKey === 'shippedList') {
            setMemberType('shipped');
            setKeyword("")
            setDates([null, null]);
        }
    };

    const initLoadOrder = async () => {
        setLoading(true);
        await getDataRequest(`${ORDERS_URL}`, {
            keyword: keyword.trim(),
            startDate: dates[0] || "",
            endDate: dates[1] || "",
            tab: memberType,
            pageNo: tableParams.pagination.current,
            sortKeys: { [tableParams.sortField]: tableParams.sortOrder === "ascend" ? "asc" : "desc" },//descend
            pageSize: tableParams.pagination.pageSize,
        }).then(res => {
            setLoading(false);
            if (res.status === 200) {
                setPaymentData(res?.data);
                setTableParams(prevParams => ({
                    ...prevParams,
                    pagination: {
                        ...prevParams.pagination,
                        total: res.total,
                    },
                }));
            }
        }).catch((err) => {
            setLoading(false);
            console.log("Error", err);
        });
    };
    
    useEffect(() => {
        isSearch && initLoadOrder();
    }, [
        tableParams.pagination?.current,
        tableParams.pagination?.pageSize,
        tableParams?.sortOrder,
        tableParams?.sortField,
        JSON.stringify(tableParams.filters), isSearch, memberType
    ]);
    
    const showAddress = ({
        recipientName,
        phone,
        addressLine1,
        addressLine2,
        city,
        district,
        province,
        country,
        zipCode,
    }) => {
        if (!recipientName || !phone) return "";

        const phoneText = `${phone.number}`;

        const addressParts = [
            addressLine1,
            addressLine2,
            district,
            city,
            province,
            zipCode,
            country,
        ].filter(Boolean); // remove empty / null values

        return `${recipientName}, ${phoneText}, ${addressParts.join(", ")}`;
    };

    const formatProducts = (items = []) => {
        return items
            .map(item => `${item.productName} x${item.qty}`)
            .join("\n");
    };

    const getOrderTotalAmount = (order) => {
        if (order?.totalAmount !== undefined && order?.totalAmount !== null) return order.totalAmount;
        if (order?.total !== undefined && order?.total !== null) return order.total;
        if (order?.amount !== undefined && order?.amount !== null) return order.amount;
        if (Array.isArray(order?.items) && order.items.length > 0) {
            return order.items.reduce((acc, item) => {
                const itemTotal = item?.subTotal !== undefined 
                    ? Number(item.subTotal) 
                    : (Number(item?.unitPrice || 0) * Number(item?.qty || 1));
                return acc + itemTotal;
            }, 0);
        }
        return 0;
    };

    const formatPrice = (order) => {
        return `￥${getOrderTotalAmount(order)}`;
    };

    const prepareExportData = (data, t) => {
        return data.map(order => ({
            [t("Order Num")]: order?.orderId,
            [t("Product Name")]: formatProducts(order.items),
            [t("User Id")]: order?.memberNo,
            [t("Payment Amount")]: formatPrice(order),
            [t("Receiving Introduction")]: showAddress(order?.shippingInfo),
            [t("Order Status")]: "已付款待发货",
            // t(
            //     order.paymentStatus === "paid"
            //         ? "Paid, waiting for shipment"
            //         : order.paymentStatus
            // ),
            [t("Payment Time")]: order?.paymentTS && formatDateTime(order?.paymentTS)
        }));
    };

    const exportToExcel = (data, t, fileName = `${memberType}-lists.xlsx`) => {
        const exportData = prepareExportData(data, t);
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
        XLSX.writeFile(workbook, fileName);
    };

    const exportOrder = async () => {
        setLoading(true);
        await getDataRequest(`${EXPORT_ORDER_URL}`, {
            keyword: keyword.trim(),
            startDate: dates[0] || "",
            endDate: dates[1] || "",
            tab: memberType,
            sortKeys: { [tableParams.sortField]: tableParams.sortOrder === "ascend" ? "asc" : "desc" },//descend
        }).then(res => {
            setLoading(false);
            if (res.status === 200) {
                setExportData(res?.data);
                exportToExcel(res.data, t);
                setTableParams({
                    ...tableParams
                });
            }
        }).catch((err) => {
            setLoading(false);
            console.log("Error", err);
        });
    };
    useEffect(() => {
        isSearchExport && exportOrder();
    }, [
        tableParams?.sortOrder,
        tableParams?.sortField,
        JSON.stringify(tableParams.filters), isSearchExport
    ]);

    return (
        <Tabs
            defaultActiveKey="pendingList"
            className="cusStyle"
            onChange={handleTabChange}
        >
            <Tabs.TabPane tab={t("Pending shipment")} key="pendingList">
                <TablePendingShipment
                    messageModalRef={messageModalRef}
                    paymentData={paymentData}
                    keyword={keyword}
                    setKeyword={setKeyword}
                    tableParams={tableParams}
                    setTableParams={setTableParams}
                    dates={dates}
                    setDates={setDates}
                    initLoadOrder={() => {
                        setIsSearch(false)
                        setTimeout(() => {
                            setIsSearch(true)
                        }, 100);
                    }}
                    memberType={memberType}
                    dataObject={dataObject} 
                    setDataObject={setDataObject}
                    isSearch={isSearch} setIsSearch={setIsSearch}
                    exportData={exportData}
                    exportOrder={() => {
                        setIsSearchExport(false)
                        setTimeout(() => {
                            setIsSearchExport(true)
                        }, 100);
                    }}
                    showAddress={showAddress}
                />
            </Tabs.TabPane>
            <Tabs.TabPane tab={t("Shipped")} key="shippedList">
                <TableShipped
                    messageModalRef={messageModalRef}
                    paymentData={paymentData}
                    keyword={keyword}
                    setKeyword={setKeyword}
                    tableParams={tableParams}
                    setTableParams={setTableParams}
                    dates={dates}
                    setDates={setDates}
                    initLoadOrder={() => {
                        setIsSearch(false)
                        setTimeout(() => {
                            setIsSearch(true)
                        }, 100);
                    }}
                    memberType={memberType}
                    dataObject={dataObject} 
                    setDataObject={setDataObject}
                    exportData={exportData}
                    exportOrder={() => {
                        setIsSearchExport(false)
                        setTimeout(() => {
                            setIsSearchExport(true)
                        }, 100);
                    }}
                    showAddress={showAddress}
                />
            </Tabs.TabPane>
        </Tabs>
    );
};

export default TableProductPurchasePage;
